import type { Ecriture } from '@shared/types/Ecriture';
import type { Facture } from '@shared/types/Facture';
import type { ModeReglement, Reglement } from '@shared/types/Reglement';
import { PLAN_COMPTABLE_OHADA } from '@shared/constants/PlanComptable';
import { roundFCFA } from '@shared/utils/round';
import { uuid } from '@shared/utils/uuid';

export function resteAPayer(facture: Facture, reglements: readonly Reglement[]): number {
  if (facture.statut === 'annulee' || facture.statut === 'annule') return 0;
  const verse = reglements
    .filter((r) => r.facture_id === facture.id)
    .reduce((s, r) => s + r.montant, 0);
  return roundFCFA(Math.max(0, (facture.total_ttc || 0) - verse));
}

/** Espèces → caisse 571 ; tout autre mode → banque 521. */
export function compteTresoPourMode(mode: ModeReglement): '521' | '571' {
  return mode === 'especes' ? '571' : '521';
}

export function journalPourMode(mode: ModeReglement): 'BQ' | 'CA' {
  return mode === 'especes' ? 'CA' : 'BQ';
}

/** Refuse montant ≤ 0 ou supérieur au reste dû (trop-perçu). */
export function validerReglement(
  facture: Facture,
  existants: readonly Reglement[],
  montant: number,
): void {
  if (!Number.isFinite(montant) || montant <= 0) {
    throw new Error('Montant de règlement invalide');
  }
  const reste = resteAPayer(facture, existants);
  if (montant > reste) {
    throw new Error(`Trop-perçu refusé (reste dû : ${reste} FCFA)`);
  }
}

export function statutApresReglements(
  facture: Facture,
  reglements: readonly Reglement[],
): Facture['statut'] {
  const reste = resteAPayer(facture, reglements);
  if (reste <= 0) return facture.type === 'achat' ? 'payee' : 'payee';
  if (reste < (facture.total_ttc || 0)) return 'payee_partiel';
  return facture.statut;
}

export function genererEcrituresReglement(
  facture: Facture,
  reglement: Reglement,
  compteTiers: string,
): Ecriture[] {
  const periode = reglement.date.slice(0, 7);
  const treso = compteTresoPourMode(reglement.mode);
  const journal = journalPourMode(reglement.mode);
  const libTreso = PLAN_COMPTABLE_OHADA[treso]?.libelle ?? 'Trésorerie';
  const libTiers = PLAN_COMPTABLE_OHADA[compteTiers]?.libelle ?? 'Tiers';
  const achat = facture.type === 'achat';
  const montant = roundFCFA(reglement.montant);
  const piece = reglement.id;
  const libelle = reglement.libelle || `Règlement ${facture.numero}`;

  const ligne = (
    compte: string,
    libelleCompte: string,
    debit: number,
    credit: number,
  ): Ecriture => ({
    id: uuid('ecr'),
    tenant_id: facture.tenant_id,
    date: reglement.date,
    periode,
    journal,
    numero_piece: piece,
    compte,
    libelle_compte: libelleCompte,
    libelle,
    debit,
    credit,
    source: 'reglement',
    facture_id: facture.id,
    groupe_id: reglement.id,
    genere_le: new Date().toISOString(),
  });

  if (achat) {
    return [
      ligne(compteTiers, libTiers, montant, 0),
      ligne(treso, libTreso, 0, montant),
    ];
  }
  return [
    ligne(treso, libTreso, montant, 0),
    ligne(compteTiers, libTiers, 0, montant),
  ];
}
